'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface Technology {
  id: string;
  name: string;
  category: 'language' | 'framework' | 'database' | 'infrastructure' | 'tool';
}

interface TechAssessment {
  id: string;
  maturity: number;
  communitySupport: number;
  talentAvailability: number;
  migrationComplexity: number;
  vendorLockIn: number;
  maintenanceBurden: number;
  securityPosture: number;
  strategicFit: number;
}

interface RiskAnalysis {
  overallRisk: number;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  riskColor: string;
  criticalFactors: string[];
  recommendations: string[];
  scores: {
    technical: number;
    organizational: number;
    strategic: number;
  };
}

export default function TechStackRiskRadarPage() {
  const [technologies, setTechnologies] = useState<Technology[]>([
    { id: 'tech1', name: 'React', category: 'framework' },
    { id: 'tech2', name: 'PostgreSQL', category: 'database' },
  ]);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'tech-stack-risk-radar',
    toolCategory: 'analyzer',
  });

  const [assessments, setAssessments] = useState<Record<string, TechAssessment>>({
    tech1: { id: 'tech1', maturity: 5, communitySupport: 5, talentAvailability: 5, migrationComplexity: 3, vendorLockIn: 1, maintenanceBurden: 2, securityPosture: 4, strategicFit: 5 },
    tech2: { id: 'tech2', maturity: 5, communitySupport: 4, talentAvailability: 4, migrationComplexity: 4, vendorLockIn: 1, maintenanceBurden: 2, securityPosture: 5, strategicFit: 4 },
  });

  const [newTechName, setNewTechName] = useState('');
  const [newTechCategory, setNewTechCategory] = useState<Technology['category']>('framework');

  const addTechnology = () => {
    if (newTechName.trim()) {
      const newId = `tech${Date.now()}`;
      setTechnologies([...technologies, { id: newId, name: newTechName.trim(), category: newTechCategory }]);
      setAssessments({
        ...assessments,
        [newId]: { id: newId, maturity: 3, communitySupport: 3, talentAvailability: 3, migrationComplexity: 3, vendorLockIn: 3, maintenanceBurden: 3, securityPosture: 3, strategicFit: 3 },
      });
      setNewTechName('');
    }
  };

  const removeTechnology = (id: string) => {
    setTechnologies(technologies.filter(t => t.id !== id));
    const newAssessments = { ...assessments };
    delete newAssessments[id];
    setAssessments(newAssessments);
  };

  const updateAssessment = (techId: string, field: keyof Omit<TechAssessment, 'id'>, value: number) => {
    setAssessments({
      ...assessments,
      [techId]: { ...assessments[techId], [field]: value },
    });
  };

  const analyzeRisk = (assessment: TechAssessment): RiskAnalysis => {
    // Risk factors (inverse for positive attributes)
    const technicalRisk = (
      (6 - assessment.maturity) * 2 +
      (6 - assessment.communitySupport) * 1.5 +
      (6 - assessment.securityPosture) * 2
    ) / 5.5;

    const organizationalRisk = (
      (6 - assessment.talentAvailability) * 2 +
      assessment.maintenanceBurden * 1.5
    ) / 3.5;

    const strategicRisk = (
      (6 - assessment.strategicFit) * 2.5 +
      assessment.vendorLockIn * 2 +
      assessment.migrationComplexity * 1.5
    ) / 6;

    const overallRisk = (technicalRisk + organizationalRisk + strategicRisk) / 3;

    let riskLevel: RiskAnalysis['riskLevel'];
    let riskColor: string;
    if (overallRisk < 2) { riskLevel = 'Low'; riskColor = 'text-green-500'; }
    else if (overallRisk < 3) { riskLevel = 'Medium'; riskColor = 'text-yellow-500'; }
    else if (overallRisk < 4) { riskLevel = 'High'; riskColor = 'text-accent'; }
    else { riskLevel = 'Critical'; riskColor = 'text-red-500'; }

    const criticalFactors: string[] = [];
    if (assessment.maturity <= 2) criticalFactors.push('Immature technology - high stability risk');
    if (assessment.communitySupport <= 2) criticalFactors.push('Weak community support - limited resources');
    if (assessment.talentAvailability <= 2) criticalFactors.push('Scarce talent - hiring challenges');
    if (assessment.migrationComplexity >= 4) criticalFactors.push('High migration complexity - lock-in risk');
    if (assessment.vendorLockIn >= 4) criticalFactors.push('Significant vendor lock-in');
    if (assessment.maintenanceBurden >= 4) criticalFactors.push('High maintenance burden');
    if (assessment.securityPosture <= 2) criticalFactors.push('Security concerns - requires attention');
    if (assessment.strategicFit <= 2) criticalFactors.push('Poor strategic fit - misaligned with goals');

    const recommendations: string[] = [];
    if (overallRisk >= 4) {
      recommendations.push('Consider alternatives or create a migration plan');
    } else if (overallRisk >= 3) {
      recommendations.push('Monitor closely and prepare contingency plans');
    }

    if (assessment.talentAvailability <= 2) {
      recommendations.push('Invest in training programs or consider more mainstream alternatives');
    }

    if (assessment.vendorLockIn >= 4 && assessment.migrationComplexity >= 4) {
      recommendations.push('High lock-in + migration complexity = long-term commitment. Ensure strategic alignment.');
    }

    if (assessment.maturity <= 2) {
      recommendations.push('Early-stage technology. Plan for breaking changes and limited ecosystem.');
    }

    if (assessment.maintenanceBurden >= 4) {
      recommendations.push('High maintenance overhead. Evaluate if benefits justify the cost.');
    }

    if (recommendations.length === 0) {
      recommendations.push('Technology appears well-suited. Continue monitoring and periodic reassessment.');
    }

    return {
      overallRisk,
      riskLevel,
      riskColor,
      criticalFactors,
      recommendations,
      scores: {
        technical: technicalRisk,
        organizational: organizationalRisk,
        strategic: strategicRisk,
      },
    };
  };

  const overallAnalysis = useMemo(() => {
    if (technologies.length === 0) return null;

    const analyses = technologies.map(tech => ({
      tech,
      analysis: analyzeRisk(assessments[tech.id]),
    }));

    const avgRisk = analyses.reduce((sum, a) => sum + a.analysis.overallRisk, 0) / analyses.length;
    const criticalTechs = analyses.filter(a => a.analysis.riskLevel === 'Critical' || a.analysis.riskLevel === 'High');
    const lowRiskTechs = analyses.filter(a => a.analysis.riskLevel === 'Low');

    return {
      avgRisk,
      criticalTechs,
      lowRiskTechs,
      allAnalyses: analyses,
    };
  }, [technologies, assessments]);

  const criteriaLabels = [
    { key: 'maturity', label: 'Technology Maturity', hint: '1 = Experimental, 5 = Battle-tested' },
    { key: 'communitySupport', label: 'Community Support', hint: '1 = Minimal, 5 = Vibrant ecosystem' },
    { key: 'talentAvailability', label: 'Talent Availability', hint: '1 = Scarce, 5 = Abundant' },
    { key: 'migrationComplexity', label: 'Migration Complexity', hint: '1 = Easy to migrate away, 5 = Very difficult' },
    { key: 'vendorLockIn', label: 'Vendor Lock-in', hint: '1 = Portable, 5 = Heavily locked-in' },
    { key: 'maintenanceBurden', label: 'Maintenance Burden', hint: '1 = Low effort, 5 = High effort' },
    { key: 'securityPosture', label: 'Security Posture', hint: '1 = Concerning, 5 = Excellent' },
    { key: 'strategicFit', label: 'Strategic Fit', hint: '1 = Misaligned, 5 = Perfect fit' },
  ];

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled || !overallAnalysis || technologies.length === 0) return;

    const gaps = overallAnalysis.allAnalyses
      .filter(({ analysis }) => analysis.riskLevel === 'Critical' || analysis.riskLevel === 'High')
      .flatMap(({ tech, analysis }) =>
        analysis.criticalFactors.map((factor, idx) => ({
          id: `${tech.id}-${idx}`,
          title: `${tech.name}: ${factor}`,
          severity: analysis.riskLevel === 'Critical' ? 'critical' as const : 'high' as const,
          description: `Risk score: ${analysis.overallRisk.toFixed(1)}/5`,
        }))
      );

    const recs = overallAnalysis.allAnalyses
      .flatMap(({ tech, analysis }) =>
        analysis.recommendations.map((rec, idx) => ({
          id: `${tech.id}-rec-${idx}`,
          title: `${tech.name}: ${rec}`,
          priority: analysis.riskLevel === 'Critical' ? 'high' as const :
                    analysis.riskLevel === 'High' ? 'high' as const : 'medium' as const,
          description: `Technology category: ${tech.category}`,
        }))
      )
      .slice(0, 10);

    const scores: Record<string, number> = {};
    overallAnalysis.allAnalyses.forEach(({ tech, analysis }) => {
      scores[tech.name.toLowerCase().replace(/\s+/g, '-')] = Math.max(0, 100 - (analysis.overallRisk * 20));
    });

    const overallScore = Math.max(0, 100 - (overallAnalysis.avgRisk * 20));

    const assessment = await saveResult({
      name: `Tech Stack Risk Assessment - ${new Date().toLocaleDateString()}`,
      score_overall: overallScore,
      scores,
      answers: assessments,
      gaps,
      recommendations: recs,
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🎯</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Tech Stack Risk Radar</h1>
          </div>
          <p className="text-xl text-foreground-secondary">Assess risks in your technology stack across multiple dimensions</p>
        </div>

        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Tech Stack Risk Radar</span>
        </nav>

        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this tool</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Add technologies from your stack (languages, frameworks, databases, etc.)</li>
            <li>Rate each technology across 8 risk dimensions</li>
            <li>Review overall risk levels and critical factors</li>
            <li>Use recommendations to inform technology decisions</li>
          </ol>
        </div>

        <div className="mb-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-xl font-bold text-foreground mb-4">Add Technology</h3>
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Technology name (e.g., React, PostgreSQL)"
              value={newTechName}
              onChange={(e) => setNewTechName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTechnology()}
              className="flex-1 px-4 py-2 bg-background border border-border rounded text-foreground"
            />
            <select
              value={newTechCategory}
              onChange={(e) => setNewTechCategory(e.target.value as Technology['category'])}
              className="px-4 py-2 bg-background border border-border rounded text-foreground"
            >
              <option value="language">Language</option>
              <option value="framework">Framework</option>
              <option value="database">Database</option>
              <option value="infrastructure">Infrastructure</option>
              <option value="tool">Tool</option>
            </select>
            <button
              onClick={addTechnology}
              className="px-6 py-2 bg-accent text-accent-foreground font-bold rounded hover:bg-accent/80 transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {technologies.map(tech => {
              const assessment = assessments[tech.id];
              const analysis = analyzeRisk(assessment);

              return (
                <div key={tech.id} className="bg-surface rounded-lg border border-border p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold text-foreground">{tech.name}</h3>
                        <span className="text-xs px-2 py-1 bg-background rounded text-foreground-secondary">{tech.category}</span>
                      </div>
                      <div className="mt-2">
                        <span className={`text-sm font-bold ${analysis.riskColor}`}>
                          Risk: {analysis.riskLevel} ({analysis.overallRisk.toFixed(1)}/5)
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeTechnology(tech.id)}
                      className="text-red-500 hover:text-red-400 text-sm font-semibold"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="space-y-3">
                    {criteriaLabels.map(criteria => (
                      <div key={criteria.key}>
                        <div className="flex justify-between mb-1">
                          <label className="text-sm text-foreground-secondary">{criteria.label}</label>
                          <span className="text-sm text-accent font-bold">
                            {assessment[criteria.key as keyof Omit<TechAssessment, 'id'>]}
                          </span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="5"
                          value={assessment[criteria.key as keyof Omit<TechAssessment, 'id'>]}
                          onChange={(e) => updateAssessment(tech.id, criteria.key as keyof Omit<TechAssessment, 'id'>, parseInt(e.target.value))}
                          className="w-full"
                        />
                        <div className="text-xs text-foreground-tertiary mt-1">{criteria.hint}</div>
                      </div>
                    ))}
                  </div>

                  {analysis.criticalFactors.length > 0 && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded">
                      <div className="text-sm font-bold text-red-400 mb-1">Critical Factors:</div>
                      <ul className="text-xs text-foreground-secondary space-y-1">
                        {analysis.criticalFactors.map((factor, idx) => (
                          <li key={idx}>• {factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}

            {technologies.length === 0 && (
              <div className="bg-surface rounded-lg border border-border p-8 text-center">
                <p className="text-foreground-secondary">Add technologies to begin your risk assessment</p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {overallAnalysis && (
              <>
                <div className="bg-surface rounded-lg border border-border p-6 sticky top-4">
                  <h3 className="text-xl font-bold text-foreground mb-4">Overall Risk</h3>
                  <div className="text-center py-4 mb-4">
                    <div className="text-5xl font-bold text-accent mb-2">
                      {overallAnalysis.avgRisk.toFixed(1)}
                    </div>
                    <div className="text-sm text-foreground-secondary">Average Risk Score</div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-secondary">Technologies:</span>
                      <span className="text-accent font-bold">{technologies.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-secondary">High Risk:</span>
                      <span className="text-red-400 font-bold">{overallAnalysis.criticalTechs.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-secondary">Low Risk:</span>
                      <span className="text-green-400 font-bold">{overallAnalysis.lowRiskTechs.length}</span>
                    </div>
                  </div>

                  {overallAnalysis.criticalTechs.length > 0 && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded">
                      <div className="text-sm font-bold text-red-400 mb-2">High Risk Technologies:</div>
                      <ul className="text-xs text-foreground-secondary space-y-1">
                        {overallAnalysis.criticalTechs.map(({ tech }) => (
                          <li key={tech.id}>• {tech.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="bg-surface rounded-lg border border-border p-6">
                  <h3 className="text-xl font-bold text-foreground mb-4">Risk Breakdown</h3>
                  <div className="space-y-3">
                    {overallAnalysis.allAnalyses.map(({ tech, analysis }) => (
                      <div key={tech.id} className="p-3 bg-background rounded border border-border-muted">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-semibold text-foreground">{tech.name}</span>
                          <span className={`text-sm font-bold ${analysis.riskColor}`}>
                            {analysis.riskLevel}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-foreground-secondary">
                          <div>Tech: {analysis.scores.technical.toFixed(1)}</div>
                          <div>Org: {analysis.scores.organizational.toFixed(1)}</div>
                          <div>Strategy: {analysis.scores.strategic.toFixed(1)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-accent-muted border border-border rounded-lg p-6">
                  <h3 className="text-lg font-bold text-accent mb-3">Recommendations</h3>
                  <div className="space-y-3">
                    {overallAnalysis.allAnalyses
                      .filter(({ analysis }) => analysis.recommendations.length > 0)
                      .map(({ tech, analysis }) => (
                        <div key={tech.id}>
                          <div className="font-semibold text-foreground mb-1">{tech.name}:</div>
                          <ul className="text-xs text-foreground-secondary space-y-1">
                            {analysis.recommendations.map((rec, idx) => (
                              <li key={idx}>• {rec}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Save to Command Center */}
                {commandCenterEnabled && !saved && (
                  <div className="mt-6">
                    <button
                      onClick={handleSaveToCommandCenter}
                      disabled={saving}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save to Command Center'}
                    </button>
                  </div>
                )}
                {saved && (
                  <div className="mt-6 px-4 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Saved to Command Center
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
